import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { RegisterData } from '../types';
import styled from 'styled-components';
import { Eye, EyeOff, ArrowLeft, Building2 } from 'lucide-react';

const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  padding: 20px;
`;

const RegisterCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
  margin-bottom: 24px;
  transition: color 0.2s ease;

  &:hover {
    color: #00d4aa;
  }
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 8px;
  color: #333;
  font-size: 28px;
`;

const Subtitle = styled.p`
  text-align: center;
  color: #666;
  margin-bottom: 32px;
  font-size: 14px;
`;

const BusinessBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  margin-bottom: 24px;
  font-weight: 600;
  font-size: 14px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 16px;
  border: 2px solid ${props => props.$hasError ? '#dc3545' : '#e9ecef'};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  transition: color 0.2s ease;

  &:hover {
    color: #495057;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: #00d4aa;
  color: white;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #00b894;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
  color: #666;
  font-size: 14px;
`;

const FooterLink = styled(Link)`
  color: #00d4aa;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

export const RegisterPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { register: registerUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<RegisterData>();
  const password = watch('password');

  // Função para formatar CPF: 000.000.000-00 ou CNPJ: 00.000.000/0000-00
  const formatCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, '');

    // CPF (11 dígitos)
    if (numbers.length <= 11) {
      if (numbers.length <= 3) return numbers;
      if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
      if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
    }

    // CNPJ (14 dígitos)
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNPJ(e.target.value);
    setValue('cnpj', formatted);
  };

  // Função para formatar telefone: (00) 00000-0000
  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setValue('phone', formatted);
  };

  const onSubmit = async (data: RegisterData) => {
    setIsLoading(true);

    try {
      await registerUser(data);
      showSuccess('Conta criada com sucesso! Redirecionando...', 4000);

      // Delay para mostrar o toast antes de navegar
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2500);

    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao criar conta';

      // Mensagens de erro mais específicas com duração maior
      if (errorMessage.includes('already exists') || errorMessage.includes('já existe')) {
        showError('Este email já está cadastrado. Tente fazer login ou use outro email.', 7000);
      } else if (errorMessage.includes('Network Error') || errorMessage.includes('Failed to fetch')) {
        showError('Erro de conexão. Verifique sua internet e tente novamente.', 7000);
      } else if (errorMessage.includes('validation') || errorMessage.includes('validação')) {
        showError('Dados inválidos. Verifique os campos e tente novamente.', 7000);
      } else {
        showError(errorMessage, 7000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <RegisterContainer>
      <RegisterCard>
        <BackButton to="/">
          <ArrowLeft size={16} />
          Voltar
        </BackButton>

        <Title>Cadastro de Empresa</Title>
        <Subtitle>Crie sua conta e comece a fidelizar clientes hoje mesmo</Subtitle>

        <BusinessBadge>
          <Building2 size={18} />
          Cadastro Empresarial
        </BusinessBadge>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <InputGroup>
            <Input
              type="text"
              placeholder="Nome do responsável *"
              $hasError={!!errors.name}
              {...register('name', {
                required: 'Nome do responsável é obrigatório',
                minLength: {
                  value: 2,
                  message: 'Nome deve ter pelo menos 2 caracteres'
                }
              })}
            />
            {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <Input
              type="text"
              placeholder="CPF ou CNPJ *"
              maxLength={18}
              $hasError={!!errors.cnpj}
              {...register('cnpj', {
                required: 'CPF ou CNPJ é obrigatório',
                validate: (value) => {
                  const numbers = value.replace(/\D/g, '');
                  if (numbers.length === 11) {
                    // Validar formato CPF: 000.000.000-00
                    return /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value) || 'CPF inválido (use formato: 000.000.000-00)';
                  } else if (numbers.length === 14) {
                    // Validar formato CNPJ: 00.000.000/0000-00
                    return /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(value) || 'CNPJ inválido (use formato: 00.000.000/0000-00)';
                  } else {
                    return 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos';
                  }
                }
              })}
              onChange={handleCNPJChange}
            />
            {errors.cnpj && <ErrorMessage>{errors.cnpj.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <Input
              type="text"
              placeholder="Nome da empresa (opcional)"
              $hasError={!!errors.company_name}
              {...register('company_name')}
            />
            {errors.company_name && <ErrorMessage>{errors.company_name.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <Input
              type="email"
              placeholder="Email corporativo *"
              $hasError={!!errors.email}
              {...register('email', {
                required: 'Email é obrigatório',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Email inválido'
                }
              })}
            />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <Input
              type="tel"
              placeholder="Telefone *"
              maxLength={15}
              $hasError={!!errors.phone}
              {...register('phone', {
                required: 'Telefone é obrigatório',
                pattern: {
                  value: /^\(\d{2}\) \d{5}-\d{4}$/,
                  message: 'Telefone inválido (use formato: (00) 00000-0000)'
                },
                minLength: {
                  value: 15,
                  message: 'Telefone deve ter 11 dígitos'
                }
              })}
              onChange={handlePhoneChange}
            />
            {errors.phone && <ErrorMessage>{errors.phone.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Senha"
              $hasError={!!errors.password}
              {...register('password', {
                required: 'Senha é obrigatória',
                minLength: {
                  value: 8,
                  message: 'Senha deve ter pelo menos 8 caracteres'
                }
              })}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirmar senha"
              $hasError={!!errors.password_confirmation}
              {...register('password_confirmation', {
                required: 'Confirmação de senha é obrigatória',
                validate: value => value === password || 'Senhas não coincidem'
              })}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
            {errors.password_confirmation && <ErrorMessage>{errors.password_confirmation.message}</ErrorMessage>}
          </InputGroup>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Criando conta...' : 'Criar conta'}
          </Button>
        </Form>

        <Footer>
          Já tem uma conta?{' '}
          <FooterLink to="/app/login">Faça login aqui</FooterLink>
        </Footer>
      </RegisterCard>
    </RegisterContainer>
  );
};
